import Dot, {
  tree
} from '@contently/dot-tree';
import Entitlements from './Entitlements';
import Entitlement from './Entitlement';
/**
 *
 * @class EntitlementMapper
 * @classdesc Transforms flat entitlements map to entitlements tree
 * eg.
 * ```
 * // permissions.js
 * {
 *    "admin" : true,
 *    "admin.users.edit" : true,
 *    "admin.users.delete" : false,
 *    "profile.edit" : true,
 *
 * }
 * =>
 * {
 *    admin: {
 *      enabled: true,
 *      users: {
 *        enabled: true,
 *        edit: { enabled: true },
 *        delete: { enabled: false }
 *      }
 *    }
 * }
 * ```
 */
export default class EntitlementMapper {
  /**
   *
   * @param {object} flatObject - the 'flat' dot-notation permission heirarchy to load
   * @param {*} resolveEntitlementFn -- the function called when a permission that does not
   * exist is requested. Takes the dot-notation permission request and should return
   * a well-formed flat entitlement.
   */
  static load(flatObject, resolveEntitlementFn = null) {
    const e = new Entitlements(null, resolveEntitlementFn);
    const parsed = Dot
      .createNodesAs(Entitlement)
      .parse(flatObject, (key, node, prevNode) => Object.assign(node, {
        name: key,
        parent: prevNode
      }));
    e.entitlements = parsed;
    return e;
  }

  /**
   *
   * @param {object} container - the container into which the permissions will
   * be mapped. This can be a POJO, or an instance of the Entitlements class.
   * @param {Entitlement} entitlement the single entitlement to map. Should be
   * in the shape of { 'dot.notation.name': { enabled: bool } }
   */
  static map(container, entitlement) {
    // TODO: Remove this backwards compatibility switch.
    // For some reason I don't remember, I made it required
    // to send in a well formed entitlement to map
    // What we required when sending in all entitlements is
    // a flat object (ie 'dot.name': { ... properties }) so
    // I think it's confusing to require something else in
    // this case...
    let candidate = entitlement;
    if (entitlement instanceof Entitlement) {
      candidate = {};
      candidate[entitlement.name] = {
        ...entitlement
      };
    }
    const newTree = Dot.createNodesAs(Entitlement).parse(candidate);
    return tree.merge(container, newTree);
  }
}
